import { useState, useEffect, useCallback, memo } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, ShoppingBag, ArrowRight, Clock, Truck, ShieldCheck, Camera, MessageCircle } from "lucide-react";
import { trackAppOpen, trackEventClick } from "../utils/analytics";
import HamburgerMenu from "../components/HamburgerMenu";
import { useCart } from "../context/CartContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Countdown hook ────────────────────────────────────────────────
const useCountdown = (endTimeISO) => {
  const [remaining, setRemaining] = useState(() => {
    if (!endTimeISO) return { text: "", expired: true };
    const diff = Math.floor((new Date(endTimeISO) - Date.now()) / 1000);
    if (diff <= 0) return { text: "Завершено", expired: true };
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    const s = diff % 60;
    const pad = (n) => n.toString().padStart(2, "0");
    return { text: `${pad(h)}:${pad(m)}:${pad(s)}`, expired: false };
  });

  useEffect(() => {
    if (!endTimeISO) return;
    const endMs = new Date(endTimeISO).getTime();
    const tick = () => {
      const diff = Math.floor((endMs - Date.now()) / 1000);
      if (diff <= 0) { setRemaining({ text: "Завершено", expired: true }); return; }
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      const pad = (n) => n.toString().padStart(2, "0");
      setRemaining({ text: `${pad(h)}:${pad(m)}:${pad(s)}`, expired: false });
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [endTimeISO]);

  return remaining;
};

// ─── Badge color map ───────────────────────────────────────────────
const BADGE_COLORS = {
  burgundy: "bg-[#8B2252]/85 text-white",
  olive: "bg-[#6B6B47]/80 text-white",
  champagne: "bg-[#C9B990]/85 text-[#1A1A1A]",
  black: "bg-black/75 text-white",
  white: "bg-white/60 text-[#1A1A1A] backdrop-blur-sm",
  gold: "bg-[#9E7C45]/85 text-white",
};
const DEFAULT_BADGE = "bg-[#6B6B47]/80 text-white";

// ─── Category chips ────────────────────────────────────────────────
const CATEGORIES = [
  { key: "all", label: "Все акции" },
  { key: "clothing", label: "Одежда" },
  { key: "shoes", label: "Обувь" },
  { key: "bags", label: "Сумки" },
  { key: "watches", label: "Часы" },
  { key: "accessories", label: "Аксессуары" },
  { key: "mens", label: "Мужское" },
];

// ─── Event Card ────────────────────────────────────────────────────
const EventCard = memo(({ event, onClick }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const { text: countdownText, expired } = useCountdown(event.eventEndTime);
  const badgeClass = event.badgeColor ? (BADGE_COLORS[event.badgeColor] || DEFAULT_BADGE) : DEFAULT_BADGE;

  return (
    <div
      onClick={onClick}
      className="relative rounded-[22px] overflow-hidden cursor-pointer h-[280px] sm:h-[320px] group"
      data-testid={`event-card-${event.id}`}
    >
      {!imageLoaded && <div className="absolute inset-0 bg-[#E8E3DE] animate-pulse" />}

      <img
        src={event.image}
        alt={event.brand}
        loading="lazy"
        className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 group-hover:scale-[1.03] ${imageLoaded ? "opacity-100" : "opacity-0"}`}
        style={{ objectPosition: event.objectPosition }}
        onLoad={() => setImageLoaded(true)}
        data-testid={`event-image-${event.id}`}
      />

      {/* Left-side gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/25 to-transparent" />

      {/* Badge */}
      {event.badge && (
        <div className="absolute top-5 left-5 z-10">
          <span
            className={`inline-block px-4 py-1.5 rounded-full text-[11px] font-sans font-semibold tracking-[0.08em] uppercase ${badgeClass}`}
            data-testid={`event-badge-${event.id}`}
          >
            {event.badge}
          </span>
        </div>
      )}

      {/* Content */}
      <div className="absolute bottom-0 left-0 p-6 z-10 max-w-[70%]">
        <h2
          className="text-white text-[26px] sm:text-[30px] font-serif font-bold tracking-tight leading-[1.05] mb-2 uppercase"
          data-testid={`event-brand-${event.id}`}
        >
          {event.brand}
        </h2>

        <div className="flex items-center gap-1.5 mb-4">
          {!expired && <Clock size={13} className="text-white/70" strokeWidth={2} />}
          <p
            className={`text-[13px] font-sans tracking-wide ${expired ? "text-white/50" : "text-white/80"}`}
            data-testid={`event-countdown-${event.id}`}
          >
            {expired ? countdownText : `${countdownText} осталось`}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center group-hover:bg-white transition-colors">
            <ArrowRight size={18} className="text-[#1A1A1A]" strokeWidth={2} />
          </div>
          <span className="text-white/90 text-[14px] font-sans font-medium tracking-wide">
            Смотреть
          </span>
        </div>
      </div>
    </div>
  );
});
EventCard.displayName = "EventCard";

// ─── Trust Strip (2x2 grid, no scroll) ─────────────────────────────
const trustItems = [
  { icon: Truck, text: "Быстрая доставка из США" },
  { icon: ShieldCheck, text: "100% оригинал гарантировано" },
  { icon: Camera, text: "Живые обзоры из Майами" },
  { icon: MessageCircle, text: "Мы всегда на связи" },
];

const TrustStrip = () => (
  <div className="grid grid-cols-4 gap-1 px-4 py-5" data-testid="trust-strip">
    {trustItems.map((item, i) => {
      const Icon = item.icon;
      return (
        <div key={i} className="flex flex-col items-center gap-1.5 text-center" data-testid={`trust-item-${i}`}>
          <Icon size={20} className="text-[#1A1A1A]/55" strokeWidth={1.5} />
          <span className="text-[10px] font-sans text-[#1A1A1A]/65 leading-tight font-semibold">
            {item.text}
          </span>
        </div>
      );
    })}
  </div>
);

// ─── Main Screen ───────────────────────────────────────────────────
const EventsScreen = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [loadPhase, setLoadPhase] = useState("loading");
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const { itemsCount } = useCart();

  const fetchEvents = useCallback(() => {
    setLoading(true);
    setFetchError(false);
    setLoadPhase("loading");
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 14000);
    fetch(`${API_URL}/api/events`, { signal: controller.signal })
      .then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
      .then((data) => { setEvents(data); setLoadPhase("loading"); })
      .catch((err) => {
        if (err.name !== "AbortError") console.error("Failed to fetch events:", err);
        setFetchError(true); setLoadPhase("failed");
      })
      .finally(() => { clearTimeout(timeout); setLoading(false); });
    return () => { clearTimeout(timeout); controller.abort(); };
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  useEffect(() => {
    if (!loading) return;
    const slowTimer = setTimeout(() => setLoadPhase("slow"), 7000);
    const failTimer = setTimeout(() => { setLoadPhase("failed"); setLoading(false); setFetchError(true); }, 14000);
    return () => { clearTimeout(slowTimer); clearTimeout(failTimer); };
  }, [loading]);

  useEffect(() => {
    if (!loading && events.length >= 0) trackAppOpen();
  }, [loading, events.length]);

  const handleEventClick = (event) => {
    trackEventClick(event.id, event.brand);
    navigate(`/event/${event.id}`);
  };

  const filteredEvents = activeCategory === "all"
    ? events
    : events.filter((e) => e.category === activeCategory);

  // ─── Header ────────────────────────────────────────────────────
  const renderHeader = () => (
    <div className="px-5 pt-3 pb-0">
      <div className="flex items-center justify-between mb-1">
        <button
          onClick={() => setMenuOpen(true)}
          className="w-10 h-10 flex items-center justify-center"
          data-testid="hamburger-menu-button"
        >
          <Menu size={24} className="text-[#1A1A1A]" strokeWidth={1.5} />
        </button>
        <button
          onClick={() => navigate("/cart")}
          className="w-10 h-10 flex items-center justify-center relative"
          data-testid="cart-button"
        >
          <ShoppingBag size={22} className="text-[#1A1A1A]" strokeWidth={1.5} />
          {itemsCount > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[#1A1A1A] text-white text-[10px] font-sans font-bold flex items-center justify-center">
              {itemsCount > 99 ? "99+" : itemsCount}
            </span>
          )}
        </button>
      </div>
      <div className="flex flex-col items-center mb-2">
        <img
          src="/shoemaster-logo.png"
          alt="Shoemaster"
          className="h-[84px] sm:h-[94px] object-contain"
          data-testid="logo"
        />
        <p className="text-[#1A1A1A]/50 text-[15px] sm:text-[16px] font-sans -mt-2.5 tracking-wide text-center italic" data-testid="subtitle">
          Лучшие обзоры бутиков и аутлетов Майами
        </p>
      </div>
    </div>
  );

  // ─── Category Filters ─────────────────────────────────────────
  const renderFilters = () => (
    <div className="flex gap-2.5 overflow-x-auto scrollbar-hide px-5 pb-4 sticky top-0 z-20 bg-[#F5F0EB] pt-1" data-testid="category-filters">
      {CATEGORIES.map((cat) => (
        <button
          key={cat.key}
          onClick={() => setActiveCategory(cat.key)}
          className={`flex-shrink-0 px-5 py-2.5 rounded-full text-[13px] font-sans font-medium transition-all duration-200 border ${
            activeCategory === cat.key
              ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
              : "bg-transparent text-[#1A1A1A]/65 border-[#1A1A1A]/12 active:bg-[#1A1A1A]/[0.04]"
          }`}
          data-testid={`filter-${cat.key}`}
        >
          {cat.label}
        </button>
      ))}
    </div>
  );

  // ─── Shell ─────────────────────────────────────────────────────
  const renderShell = (children) => (
    <div className="h-[100dvh] overflow-y-auto bg-[#F5F0EB] scrollbar-hide" data-testid="events-screen">
      {renderHeader()}
      {!loading && !fetchError && renderFilters()}
      {children}
      <HamburgerMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
    </div>
  );

  // ─── Error ─────────────────────────────────────────────────────
  if (fetchError && !loading) {
    return renderShell(
      <div className="px-4 pb-10 flex flex-col items-center gap-4 pt-16" data-testid="events-error">
        <p className="text-[#1A1A1A]/70 text-sm font-sans text-center leading-relaxed">
          Не удалось загрузить страницу.<br />Попробуйте ещё раз.
        </p>
        <button
          onClick={() => fetchEvents()}
          className="px-8 py-3 bg-[#1A1A1A] text-white text-sm font-sans font-medium rounded-full active:scale-[0.97] transition-transform"
          data-testid="retry-button"
        >
          Повторить
        </button>
      </div>
    );
  }

  // ─── Loading ───────────────────────────────────────────────────
  if (loading) {
    return renderShell(
      <div className="px-5 pb-10 flex flex-col gap-4">
        {[280, 280, 280].map((h, i) => (
          <div key={i} className="rounded-[22px] bg-[#E8E3DE] animate-pulse" style={{ height: h }} />
        ))}
        {loadPhase === "slow" && (
          <p className="text-center text-[#1A1A1A]/50 text-xs font-sans mt-4" data-testid="slow-loading-message">
            Загрузка занимает больше времени, чем обычно
          </p>
        )}
      </div>
    );
  }

  // ─── Empty ─────────────────────────────────────────────────────
  if (filteredEvents.length === 0 && activeCategory !== "all") {
    return renderShell(
      <div className="px-5 pb-10">
        <div className="flex flex-col items-center gap-3 pt-12" data-testid="events-empty-category">
          <p className="text-[#1A1A1A]/40 text-sm font-sans text-center">Нет акций в этой категории</p>
        </div>
        <TrustStrip />
      </div>
    );
  }

  if (events.length === 0) {
    return renderShell(
      <div className="px-5 pb-10 flex flex-col items-center gap-4 pt-16" data-testid="events-empty">
        <p className="text-[#1A1A1A]/50 text-sm font-sans text-center">Сейчас нет активных акций</p>
      </div>
    );
  }

  // ─── Success ───────────────────────────────────────────────────
  return renderShell(
    <div className="pb-6">
      <div className="px-5 flex flex-col gap-4">
        {filteredEvents.map((event) => (
          <EventCard key={event.id} event={event} onClick={() => handleEventClick(event)} />
        ))}
      </div>
      <TrustStrip />
    </div>
  );
};

export default EventsScreen;
